/**
 * Analytics Page
 * Professional Admin Portal Design
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../components/shared/Toast';
import { cn } from '../../lib/utils';
import { authenticatedFetch } from '../../lib/api';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { MdTrendingUp, MdBusinessCenter, MdPeople, MdVpnKey, MdDownload, MdRefresh } from 'react-icons/md';
import { PageSkeleton } from '@euroasiann/shared/ui';

type AnalyticsSummary = {
  totalOrgs: number;
  customerCount: number;
  vendorCount: number;
  totalLicenses: number;
  activeLicenses: number;
  totalUsers: number;
  growthRate: string;
  orgTypeData: Array<{ name: string; value: number; color: string }>;
  licenseStatusData: Array<{ name: string; value: number; color: string }>;
  monthlyGrowthData: Array<{ name: string; customers: number; vendors: number }>;
  activityData: Array<{ name: string; activities: number }>;
};

const EMPTY_ANALYTICS: AnalyticsSummary = {
  totalOrgs: 0,
  customerCount: 0,
  vendorCount: 0,
  totalLicenses: 0,
  activeLicenses: 0,
  totalUsers: 0,
  growthRate: '0.0',
  orgTypeData: [
    { name: 'Customer', value: 0, color: '#3b82f6' },
    { name: 'Vendor', value: 0, color: '#8b5cf6' },
  ],
  licenseStatusData: [],
  monthlyGrowthData: [],
  activityData: [],
};

export function AnalyticsPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: analytics = EMPTY_ANALYTICS, isLoading } = useQuery({
    queryKey: ['admin-analytics-summary'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/v1/admin/analytics/summary');
      if (!response.ok) throw new Error('Failed to load analytics');
      const json = await response.json();
      return (json.data || EMPTY_ANALYTICS) as AnalyticsSummary;
    },
  });

  const handleRefresh = () => {
    setIsRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ['admin-analytics-summary'] });
    setTimeout(() => {
      setIsRefreshing(false);
      showToast('Analytics data refreshed', 'success');
    }, 1000);
  };

  const handleExport = () => {
    showToast('Export functionality will be implemented soon', 'info');
    // TODO: Implement export functionality
  };

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-1">Analytics & Insights</h1>
          <p className="text-[hsl(var(--muted-foreground))]">Platform analytics and performance metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors text-sm font-medium text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdRefresh className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0088cc] hover:bg-[#0077b3] transition-all hover:bg-[#0077b3] text-white transition-colors font-semibold text-sm shadow-lg shadow-[#0088cc]/30"
          >
            <MdDownload className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">Total Organizations</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : analytics.totalOrgs.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {analytics.customerCount} customers, {analytics.vendorCount} vendors
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[#0088cc]/10 text-[#0088cc] flex items-center justify-center shadow-md">
              <MdBusinessCenter className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">Active Licenses</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : analytics.activeLicenses.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                {analytics.totalLicenses} total licenses
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-emerald-500/10 text-emerald-500 shadow-none flex items-center justify-center shadow-md">
              <MdVpnKey className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">Total Users</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : analytics.totalUsers.toLocaleString()}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Platform users</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-purple-500/10 text-purple-500 shadow-none flex items-center justify-center shadow-md">
              <MdPeople className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">Growth Rate</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {isLoading ? '...' : `${analytics.growthRate}%`}
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Platform growth</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-orange-500/10 text-orange-500 shadow-none flex items-center justify-center shadow-md">
              <MdTrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Organization Type Distribution */}
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Organization Types</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Distribution of organization types</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-[#0088cc]/10 text-[#0088cc] flex items-center justify-center shadow-md">
              <MdBusinessCenter className="w-5 h-5 text-white" />
            </div>
          </div>
          {isLoading ? (
            <PageSkeleton variant="panel" className="h-[250px]" />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              {analytics.orgTypeData.some(d => d.value > 0) ? (
                <PieChart>
                  <Pie
                    data={analytics.orgTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {analytics.orgTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[hsl(var(--muted-foreground))]">No data available</p>
                </div>
              )}
            </ResponsiveContainer>
          )}
        </div>

        {/* License Status */}
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">License Status</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">License status breakdown</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-500 shadow-none flex items-center justify-center shadow-md">
              <MdVpnKey className="w-5 h-5 text-white" />
            </div>
          </div>
          {isLoading ? (
            <PageSkeleton variant="panel" className="h-[250px]" />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              {analytics.licenseStatusData.some(d => d.value > 0) ? (
                <PieChart>
                  <Pie
                    data={analytics.licenseStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {analytics.licenseStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[hsl(var(--muted-foreground))]">No data available</p>
                </div>
              )}
            </ResponsiveContainer>
          )}
        </div>

        {/* Monthly Growth */}
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Monthly Growth</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Organization growth over time</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 text-purple-500 shadow-none flex items-center justify-center shadow-md">
              <MdTrendingUp className="w-5 h-5 text-white" />
            </div>
          </div>
          {isLoading ? (
            <PageSkeleton variant="panel" className="h-[250px]" />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={analytics.monthlyGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Bar dataKey="customers" fill="#3b82f6" radius={[8, 8, 0, 0]} name="Customers" />
                <Bar dataKey="vendors" fill="#8b5cf6" radius={[8, 8, 0, 0]} name="Vendors" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Activity Timeline */}
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-1">Weekly Activity</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Platform activity overview</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-orange-500/10 text-orange-500 shadow-none flex items-center justify-center shadow-md">
              <MdTrendingUp className="w-5 h-5 text-white" />
            </div>
          </div>
          {isLoading ? (
            <PageSkeleton variant="panel" className="h-[250px]" />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.activityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  contentStyle={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Line type="monotone" dataKey="activities" stroke="#f59e0b" strokeWidth={2} activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}




